import { useRef, useState } from "react";
import { UploadCloud, FileText, Image as ImageIcon, Trash2, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const statusColor = (s) =>
  ({ Completed: "bg-ok text-ok-foreground border-green-200",
     "Needs Review": "bg-warn text-warn-foreground border-warn-border",
     Failed: "bg-red-50 text-red-700 border-red-200",
     Processing: "bg-secondary text-secondary-foreground border-blue-200" }[s] ||
   "bg-muted text-muted-foreground border-border");

export const FileDropzone = ({ files, setFiles, statuses = {}, max = 10, accept = ".jpg,.jpeg,.png,.webp,.pdf" }) => {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const dragIndex = useRef(null);

  const add = (list) => {
    const incoming = Array.from(list).filter((f) => /\.(jpe?g|png|webp|pdf)$/i.test(f.name));
    setFiles((prev) => [...prev, ...incoming].slice(0, max));
  };

  const reorder = (to) => {
    const from = dragIndex.current;
    if (from === null || from === to) return;
    setFiles((prev) => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
    dragIndex.current = null;
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => { e.preventDefault(); setHover(false); add(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="file-dropzone"
        className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center transition-colors duration-200 ${
          hover ? "border-primary bg-secondary" : "border-border bg-white hover:border-primary/50"}`}
      >
        <UploadCloud className="mx-auto h-8 w-8 text-primary" />
        <p className="mt-4 font-display text-lg font-semibold">Drop files here or click to browse</p>
        <p className="mt-1 text-sm text-muted-foreground">
          JPG, JPEG, PNG, WEBP or PDF · up to {max} files per batch · max 15 MB each
        </p>
        <input ref={inputRef} type="file" multiple accept={accept} className="hidden"
               onChange={(e) => add(e.target.files)} data-testid="file-input" />
      </div>

      {files.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="file-cards">
          {files.map((f, i) => {
            const st = statuses[f.name] || { status: "Waiting" };
            const isPdf = /\.pdf$/i.test(f.name);
            return (
              <div key={`${f.name}-${i}`} draggable
                   onDragStart={() => { dragIndex.current = i; }}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={() => reorder(i)}
                   className="flex gap-3 rounded-lg border border-border bg-white p-3"
                   data-testid={`file-card-${i}`}>
                <GripVertical className="mt-1 h-4 w-4 shrink-0 cursor-grab text-muted-foreground" />
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                  {isPdf ? <FileText className="h-6 w-6 text-primary" />
                         : <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium" title={f.name}>{f.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {kb(f.size)}{st.pages ? ` · ${st.pages} page(s)` : ""}
                    {st.confidence ? ` · OCR ${Math.round(st.confidence * 100)}%` : ""}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline" className={statusColor(st.status)}>{st.status}</Badge>
                    {st.status === "Processing" && <Progress value={65} className="h-1 w-16" />}
                  </div>
                  {st.error && <p className="mt-1 text-xs text-red-600">{st.error}</p>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                        className="self-start" data-testid={`remove-file-${i}`} title="Remove">
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-600" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {files.length === 0 && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <ImageIcon className="h-3 w-3" /> Multi-page statements: add each page as a separate file — pages are combined automatically.
        </p>
      )}
    </div>
  );
};
